/**
 * Expense Type Help Component
 *
 * A help tooltip component that shows detailed information about expense types,
 * including descriptions, examples, and account numbers. Can be displayed as
 * a tooltip or inline help text.
 */

import { renderIcon } from '../utils/icons.js';
import {
  getExpenseTypeByCode,
  WYDEN_EXPENSE_TYPES,
  type WydenExpenseTypeDefinition
} from '../constants/expense-types.js';

/**
 * Options for ExpenseTypeHelp component
 */
export interface ExpenseTypeHelpOptions {
  /** Unique identifier for the element */
  id?: string;
  /** Expense type code to show help for (optional - if not provided, shows general help) */
  expenseTypeCode?: number;
  /** Display mode - tooltip shows on hover, inline shows always */
  mode?: 'tooltip' | 'inline';
  /** Whether to show examples */
  showExamples?: boolean;
  /** Whether to show account numbers */
  showAccountNumber?: boolean;
  /** CSS class name(s) */
  className?: string;
  /** Additional HTML attributes */
  attributes?: Record<string, string>;
}

/**
 * Generate a unique ID if not provided
 */
function generateId(): string {
  return `expense-type-help-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Create help content for a specific expense type
 */
function createExpenseTypeContent(expenseType: WydenExpenseTypeDefinition, options: ExpenseTypeHelpOptions): HTMLElement {
  const { showExamples = true, showAccountNumber = true } = options;

  const container = document.createElement('div');
  container.className = 'space-y-2';

  // Title
  const title = document.createElement('h4');
  title.className = 'font-medium text-foreground';
  title.textContent = expenseType.name;
  container.appendChild(title);

  // Account number
  if (showAccountNumber) {
    const accountDiv = document.createElement('div');
    accountDiv.className = 'text-sm text-muted-foreground';
    accountDiv.innerHTML = `<strong>Account:</strong> ${expenseType.accountNumber}`;
    container.appendChild(accountDiv);
  }

  // Description
  const description = document.createElement('p');
  description.className = 'text-sm text-muted-foreground';
  description.textContent = expenseType.description;
  container.appendChild(description);

  // Examples
  if (showExamples && expenseType.examples.length > 0) {
    const examplesDiv = document.createElement('div');
    examplesDiv.className = 'text-sm';

    const examplesTitle = document.createElement('p');
    examplesTitle.className = 'font-medium text-muted-foreground mb-1';
    examplesTitle.textContent = 'Examples:';
    examplesDiv.appendChild(examplesTitle);

    const examplesList = document.createElement('ul');
    examplesList.className = 'text-xs text-muted-foreground space-y-0.5 pl-4';

    expenseType.examples.forEach(example => {
      const item = document.createElement('li');
      item.className = 'list-disc';
      item.textContent = example;
      examplesList.appendChild(item);
    });

    examplesDiv.appendChild(examplesList);
    container.appendChild(examplesDiv);
  }

  return container;
}

/**
 * Create general help content for all expense types
 */
function createGeneralHelpContent(options: ExpenseTypeHelpOptions): HTMLElement {
  const { showAccountNumber = true } = options;

  const container = document.createElement('div');
  container.className = 'space-y-3 max-w-sm';

  // Introduction
  const intro = document.createElement('p');
  intro.className = 'text-sm text-muted-foreground';
  intro.textContent = 'Choose the expense type that best matches your receipt. Each type has a specific account number for proper categorization.';
  container.appendChild(intro);

  // Types list
  const typesList = document.createElement('div');
  typesList.className = 'space-y-2';

  Object.values(WYDEN_EXPENSE_TYPES).forEach(expenseType => {
    const typeDiv = document.createElement('div');
    typeDiv.className = 'border-l-2 border-primary/30 pl-3 py-1';

    const typeName = document.createElement('p');
    typeName.className = 'text-sm font-medium text-foreground';
    typeName.textContent = showAccountNumber
      ? `${expenseType.name} (${expenseType.accountNumber})`
      : expenseType.name;
    typeDiv.appendChild(typeName);

    const typeDesc = document.createElement('p');
    typeDesc.className = 'text-xs text-muted-foreground';
    typeDesc.textContent = expenseType.description;
    typeDiv.appendChild(typeDesc);

    typesList.appendChild(typeDiv);
  });

  container.appendChild(typesList);
  return container;
}

/**
 * Create an expense type help element
 */
export function createExpenseTypeHelp(options: ExpenseTypeHelpOptions): HTMLElement {
  const {
    id = generateId(),
    expenseTypeCode,
    mode = 'tooltip',
    className = '',
    attributes = {}
  } = options;

  if (mode === 'tooltip') {
    // Create tooltip trigger button
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `inline-flex items-center justify-center w-4 h-4 text-muted-foreground hover:text-foreground transition-colors ${className}`;
    button.setAttribute('aria-label', 'Show expense type help');
    button.id = id;

    // Add info icon
    const icon = renderIcon('info', { size: 'sm' });
    button.appendChild(icon);

    // Create tooltip content
    const tooltip = document.createElement('div');
    tooltip.className = `absolute z-50 hidden bg-popover border border-border rounded-lg shadow-lg p-3 min-w-0 max-w-md`;
    tooltip.setAttribute('role', 'tooltip');
    tooltip.setAttribute('aria-labelledby', id);

    // Position tooltip above button by default
    tooltip.style.bottom = '100%';
    tooltip.style.left = '50%';
    tooltip.style.transform = 'translateX(-50%)';
    tooltip.style.marginBottom = '8px';

    // Generate content
    if (expenseTypeCode && getExpenseTypeByCode(expenseTypeCode)) {
      const expenseType = getExpenseTypeByCode(expenseTypeCode)!;
      tooltip.appendChild(createExpenseTypeContent(expenseType, options));
    } else {
      tooltip.appendChild(createGeneralHelpContent(options));
    }

    // Create container with relative positioning
    const container = document.createElement('div');
    container.className = 'relative inline-block';
    container.appendChild(button);
    container.appendChild(tooltip);

    // Show/hide tooltip on hover
    let showTimeout: number;
    let hideTimeout: number;

    const showTooltip = () => {
      clearTimeout(hideTimeout);
      showTimeout = window.setTimeout(() => {
        tooltip.classList.remove('hidden');
      }, 200);
    };

    const hideTooltip = () => {
      clearTimeout(showTimeout);
      hideTimeout = window.setTimeout(() => {
        tooltip.classList.add('hidden');
      }, 100);
    };

    button.addEventListener('mouseenter', showTooltip);
    button.addEventListener('mouseleave', hideTooltip);
    tooltip.addEventListener('mouseenter', showTooltip);
    tooltip.addEventListener('mouseleave', hideTooltip);

    // Show/hide on focus/blur for keyboard accessibility
    button.addEventListener('focus', showTooltip);
    button.addEventListener('blur', hideTooltip);

    // Set additional attributes
    for (const [key, val] of Object.entries(attributes)) {
      container.setAttribute(key, val);
    }

    return container;
  } else {
    // Inline mode - show content directly
    const container = document.createElement('div');
    container.className = `${className}`.trim();
    container.id = id;

    // Generate content
    if (expenseTypeCode && getExpenseTypeByCode(expenseTypeCode)) {
      const expenseType = getExpenseTypeByCode(expenseTypeCode)!;
      container.appendChild(createExpenseTypeContent(expenseType, options));
    } else {
      container.appendChild(createGeneralHelpContent(options));
    }

    // Set additional attributes
    for (const [key, val] of Object.entries(attributes)) {
      container.setAttribute(key, val);
    }

    return container;
  }
}

/**
 * ExpenseTypeHelp class for object-oriented usage
 */
export class ExpenseTypeHelp {
  private element: HTMLElement;
  private options: ExpenseTypeHelpOptions;

  constructor(options: ExpenseTypeHelpOptions) {
    this.options = { ...options };
    this.element = createExpenseTypeHelp(this.options);
  }

  /**
   * Get the element
   */
  getElement(): HTMLElement {
    return this.element;
  }

  /**
   * Update the expense type being shown
   */
  setExpenseType(code: number | null): this {
    if (code !== null) {
      this.options.expenseTypeCode = code;
    } else {
      delete this.options.expenseTypeCode;
    }

    // Recreate the element with new content
    const newElement = createExpenseTypeHelp(this.options);
    if (this.element.parentNode) {
      this.element.parentNode.replaceChild(newElement, this.element);
    }
    this.element = newElement;

    return this;
  }

  /**
   * Show the help (for tooltip mode)
   */
  show(): this {
    if (this.options.mode === 'tooltip') {
      const tooltip = this.element.querySelector('[role="tooltip"]') as HTMLElement;
      if (tooltip) {
        tooltip.classList.remove('hidden');
      }
    }
    return this;
  }

  /**
   * Hide the help (for tooltip mode)
   */
  hide(): this {
    if (this.options.mode === 'tooltip') {
      const tooltip = this.element.querySelector('[role="tooltip"]') as HTMLElement;
      if (tooltip) {
        tooltip.classList.add('hidden');
      }
    }
    return this;
  }

  /**
   * Toggle visibility (for tooltip mode)
   */
  toggle(): this {
    if (this.options.mode === 'tooltip') {
      const tooltip = this.element.querySelector('[role="tooltip"]') as HTMLElement;
      if (tooltip) {
        tooltip.classList.toggle('hidden');
      }
    }
    return this;
  }
}